import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const CurrencyContext = createContext(null);

const SYM = { USD: "$", NGN: "₦" };

export function CurrencyProvider({ children, primary = "USD" }) {
  // iter 72h — user preference: FX conversion disabled everywhere. Balances
  // and reports always render in the currency each transaction was recorded
  // in. Legacy `ledger.convertOn` in localStorage is ignored and cleaned up.
  const [convertOn] = useState(false);
  const setConvertOn = () => {};
  const [rates, setRates] = useState({}); // rates keyed by base currency -> {CUR: rate}

  useEffect(() => {
    try { localStorage.removeItem("ledger.convertOn"); } catch { /* no-op */ }
  }, []);

  const ensureRates = useCallback(async (base) => {
    if (rates[base]) return rates[base];
    const { data } = await api.get(`/fx/rates?base=${base}`);
    setRates((r) => ({ ...r, [base]: data.rates }));
    return data.rates;
  }, [rates]);

  // iter 72h — the effect that persisted convertOn and fetched primary
  // rates is gone; conversion is a no-op now.

  const convert = (amount, from, to) => {
    if (!from || !to || from === to) return { amount, currency: to || from };
    const src = rates[from];
    if (src && typeof src[to] === "number") {
      return { amount: amount * src[to], currency: to };
    }
    // trigger a fetch in background if missing
    ensureRates(from).catch(() => {});
    return { amount, currency: from };
  };

  const format = (amount, currency) => {
    const sym = SYM[currency] || "";
    return `${sym}${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const displayAmount = (amount, txCurrency) => {
    if (!convertOn) return format(amount, txCurrency);
    const c = convert(amount, txCurrency, primary);
    return format(c.amount, c.currency);
  };

  return (
    <CurrencyContext.Provider value={{ convertOn, setConvertOn, rates, primary, format, convert, displayAmount, ensureRates }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
